import { Badge, PageContainer, PageIntroCard, PageSectionCard } from '@kmgestao/ui';

import { useIsSuperadmin, useSession } from '../../features/auth/index.js';
import { decodeJwtClaims } from '../../features/auth/jwt-claims.js';
import { ClinicDashboardPage } from '../clinic/dashboard/ClinicDashboardPage.js';
import { SuperadminShell } from '../superadmin/SuperadminShell.js';

/**
 * Router do `/dashboard` por contexto:
 *   - **Superadmin SEM `current_clinic_id`** (Karla acessou /dashboard
 *     sem ter clínica vinculada como membro) → placeholder informativo
 *     com link pra `/superadmin/dashboard`.
 *   - **Qualquer um COM `current_clinic_id`** (owner regular OU Karla
 *     testando como membro de uma clínica) → `<ClinicDashboardPage>`.
 *
 * **Bug fix 2026-04-26**: antes o critério era só `is_superadmin`. Isso
 * quebrava o fluxo do owner que ativava conta no mesmo browser onde
 * Karla estava logada — o JWT do welcome ainda era da Karla, e o owner
 * (depois de redirect via /welcome) caía no placeholder em vez do
 * ClinicDashboardPage. Agora `current_clinic_id` é o sinal autoritativo:
 * se tem clinic_id, mostra dashboard da clínica; senão, placeholder de
 * superadmin (único caso onde superadmin não tem clinic vinculada).
 *
 * Por que NÃO redirecionar superadmin pra `/superadmin/dashboard`
 * automaticamente: Karla pode chegar aqui legitimamente acessando
 * `/dashboard` direto sem selecionar clínica — placeholder mostra link.
 */
export function DashboardPage() {
  const isSuperadmin = useIsSuperadmin();
  const { session } = useSession();
  const claims = decodeJwtClaims(session?.access_token);
  const hasClinicContext = Boolean(claims?.current_clinic_id);

  if (isSuperadmin && !hasClinicContext) {
    return <SuperadminDashboardPlaceholder />;
  }
  return <ClinicDashboardPage />;
}

/**
 * Placeholder do dashboard do superadmin — mostra info de debug e
 * aponta pra `/superadmin/dashboard` (tela real). Conteúdo de KPIs do
 * superadmin entra em PR futura (M1-03 Dashboard KPIs).
 */
function SuperadminDashboardPlaceholder() {
  const { user, aal } = useSession();

  return (
    <SuperadminShell>
      <PageContainer>
        <PageIntroCard
          eyebrow="Início (superadmin)"
          title={`Olá, ${user?.email ?? 'usuário'}.`}
          description="Dashboard de superadmin em construção. KPIs, funil e billing chegam em M1-03. Para o painel principal de superadmin, acesse /superadmin/dashboard."
          meta={
            <>
              <Badge variant="secondary">Sessão ativa</Badge>
              <Badge variant="outline">{aal ? `MFA: ${aal.toUpperCase()}` : 'MFA: —'}</Badge>
            </>
          }
        />

        <PageSectionCard
          title="Sessão atual"
          description="Debug — visível enquanto auth está em desenvolvimento."
          contentClassName="space-y-2 text-sm"
        >
          <p>
            <span className="text-muted-foreground">E-mail:</span>{' '}
            <span className="font-mono">{user?.email ?? '—'}</span>
          </p>
          <p>
            <span className="text-muted-foreground">User ID:</span>{' '}
            <span className="font-mono text-xs">{user?.id ?? '—'}</span>
          </p>
          <p>
            <span className="text-muted-foreground">AAL (MFA level):</span>{' '}
            <span className="font-mono">{aal ?? '—'}</span>
          </p>
        </PageSectionCard>
      </PageContainer>
    </SuperadminShell>
  );
}
